import { create } from 'zustand'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string
  title?: string
  duration?: number
}

interface UiState {
  // Toasts
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  clearToasts: () => void

  // Modals
  modals: string[]
  openModal: (id: string) => void
  closeModal: (id: string) => void
  isModalOpen: (id: string) => boolean

  // Sidebar
  isSidebarOpen: boolean
  isSidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void

  // Global loading
  globalLoading: boolean
  globalLoadingMessage: string | null
  setGlobalLoading: (loading: boolean, message?: string) => void

  // Mobile menu
  isMobileMenuOpen: boolean
  setMobileMenuOpen: (open: boolean) => void
  toggleMobileMenu: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 9)

export const useUiStore = create<UiState>((set, get) => ({
  // Toasts
  toasts: [],

  addToast: (toast) => {
    const id = generateId()
    const duration = toast.duration ?? 5000

    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))

    // Auto-remove after duration (if duration > 0)
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id)
      }, duration)
    }

    return id
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearToasts: () => set({ toasts: [] }),

  // Modals
  modals: [],

  openModal: (id) =>
    set((state) => ({
      modals: state.modals.includes(id) ? state.modals : [...state.modals, id],
    })),

  closeModal: (id) =>
    set((state) => ({
      modals: state.modals.filter((m) => m !== id),
    })),

  isModalOpen: (id) => get().modals.includes(id),

  // Sidebar - Initialize based on screen width (mobile = closed, desktop = open)
  isSidebarOpen: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  isSidebarCollapsed: false,

  toggleSidebar: () =>
    set((state) => ({
      isSidebarOpen: !state.isSidebarOpen,
    })),

  setSidebarOpen: (open) => set({ isSidebarOpen: open }),

  setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

  // Global loading
  globalLoading: false,
  globalLoadingMessage: null,

  setGlobalLoading: (loading, message) =>
    set({
      globalLoading: loading,
      globalLoadingMessage: message ?? null,
    }),

  // Mobile menu
  isMobileMenuOpen: false,

  setMobileMenuOpen: (open) => set({ isMobileMenuOpen: open }),

  toggleMobileMenu: () =>
    set((state) => ({
      isMobileMenuOpen: !state.isMobileMenuOpen,
    })),
}))

// Convenience toast helpers
export const toast = {
  success: (message: string, title?: string) =>
    useUiStore.getState().addToast({ type: 'success', message, title }),

  error: (message: string, title?: string) =>
    useUiStore.getState().addToast({ type: 'error', message, title, duration: 8000 }),

  warning: (message: string, title?: string) =>
    useUiStore.getState().addToast({ type: 'warning', message, title }),

  info: (message: string, title?: string) =>
    useUiStore.getState().addToast({ type: 'info', message, title }),
}
